/* This example requires Tailwind CSS v2.0+ */
import React, { useContext, useEffect, useMemo, useState } from "react";

import { OrgContext } from "contexts/OrgContext";
import {
  ArrowsRightLeftIcon,
  EllipsisHorizontalIcon,
  UserMinusIcon,
  UserPlusIcon,
} from "@heroicons/react/24/solid";
import { Input } from "components/Graphs/Input";
import { UserContext } from "contexts/UserContext";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { Spinner } from "@kui-react/spinner";
import { OrgRoleIds } from "models/Role.model";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/Graphs/Dropdown";
import { toast } from "utils/toast";
import {
  BREV_SUPPORT_EMAIL,
  LOCALSTORAGE_ACTIVE_ORG_ID_KEY,
} from "constants/index";
import { getRoleDisplayName, isOrgAdminRole } from "utils/roleUtils";
import {
  organizationAccessClient,
  type OrganizationMemberAccess,
} from "server/devplane/OrganizationAccesses";
import {
  organizationAccessLevelToOrgRole,
  orgRoleToOrganizationAccessLevel,
} from "utils/organizationAccess";
import { transferOwnership } from "server/devplane/organizations/api";
import {
  TeamTable,
  TeamTableBody,
  TeamTableCell,
  TeamTableHead,
  TeamTableHeaderCell,
} from "./TeamTable";
import { TransferOwnershipDialog } from "./TransferOwnershipDialog";
import { usesNcaUserManagement } from "./userManagement";

interface TeamMember {
  name: string;
  email: string;
  id: string;
  username: string;
  level?: OrgRoleIds;
  access: OrganizationMemberAccess;
}

interface TeamMembersProps {
  showActions?: boolean;
}

const TeamMembers: React.FC<TeamMembersProps> = ({
  showActions: showActionsProp,
}) => {
  const orgContext = useContext(OrgContext);
  const userContext = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [allUsers, setAllUsers] = useState<TeamMember[]>([]);
  const [transferMember, setTransferMember] = useState<TeamMember | null>(null);
  const [isTransferring, setIsTransferring] = useState(false);
  const showActions =
    showActionsProp ?? isOrgAdminRole(orgContext.activeOrgRole);

  const loadMembers = async (organizationId: string) => {
    setIsLoading(true);
    try {
      const res = await organizationAccessClient.listMembers(organizationId);
      setAllUsers(
        res.members.map((member) => ({
          name: member.displayName,
          email: member.defaultEmail,
          id: member.userId,
          username: member.defaultEmail,
          level: organizationAccessLevelToOrgRole(member.accessLevel),
          access: member,
        })),
      );
    } catch (error) {
      console.log("Unable to load users");
      setAllUsers([]);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (orgContext.activeOrgId) {
      loadMembers(orgContext.activeOrgId);
    }
  }, [orgContext.activeOrgId]);

  const moveOwnertoFront = (
    array: TeamMember[],
    ownerIndex: number,
    targetEmail: string,
  ): TeamMember[] => {
    let index = ownerIndex;
    if (ownerIndex < 0) {
      index = array.findIndex((item) => item.email === targetEmail);
    }
    if (index > 0) {
      const target = array[index];
      array.splice(index, 1);
      array.unshift(target);
    }
    return array;
  };

  const filterUsers = (users: TeamMember[], searchValue) =>
    users.filter((item) => {
      if (!searchValue) return true;
      return (
        item.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        item.email.toLowerCase().includes(searchValue.toLowerCase())
      );
    });

  const displayedUsers = useMemo(() => {
    const ownerIndex = allUsers.findIndex(
      (user) => user.level === OrgRoleIds.Owner,
    );
    const formattedUsers = moveOwnertoFront([...allUsers], ownerIndex, "");

    return filterUsers(formattedUsers, searchValue);
  }, [allUsers, searchValue]);

  const confirmOwnershipTransfer = async () => {
    if (!transferMember || isTransferring) {
      return;
    }

    setIsTransferring(true);
    try {
      await transferOwnership({
        organizationId: transferMember.access.organizationId,
        ownerUserId: transferMember.id,
      });
      await Promise.allSettled([
        loadMembers(transferMember.access.organizationId),
        orgContext.refreshOrgAccesses(),
      ]);
      toast.success("Ownership transferred", {
        description: `${transferMember.name || transferMember.email} is now the organization owner.`,
      });
      setTransferMember(null);
    } catch (error) {
      toast.error("Unable to transfer ownership", {
        description: `An unexpected error occurred. Contact support at ${BREV_SUPPORT_EMAIL}.`,
      });
    } finally {
      setIsTransferring(false);
    }
  };

  return (
    <div className="flex w-full flex-col dark:text-secondary">
      <div className="flex flex-col">
        <div className="flex flex-row mb-3">
          <Input
            className="min-w-[300px]"
            type="search"
            placeholder="Search team members"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            aria-label="Search team members"
          />
        </div>
      </div>
      {isLoading && (
        <div className="mt-5">
          <Spinner size="medium" />
        </div>
      )}
      {!isLoading && (
        <TeamTable>
          <TeamTableHead>
            <tr>
              <TeamTableHeaderCell />
              <TeamTableHeaderCell>Name</TeamTableHeaderCell>
              <TeamTableHeaderCell>Email</TeamTableHeaderCell>
              <TeamTableHeaderCell>Level</TeamTableHeaderCell>
              {showActions && <TeamTableHeaderCell />}
            </tr>
          </TeamTableHead>
          <TeamTableBody>
            {displayedUsers.map((item, index, list) => (
              // eslint-disable-next-line no-use-before-define
              <TeamMemberRow
                key={item.id}
                item={item}
                index={index}
                displaylist={list}
                allUsers={allUsers}
                setAllUsers={setAllUsers}
                showActions={showActions}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                loadMembers={loadMembers}
                canTransferOwnership={
                  orgContext.activeOrgRole === OrgRoleIds.Owner &&
                  item.id !== userContext.me?.id &&
                  !usesNcaUserManagement(orgContext.activeOrg)
                }
                onTransferOwnership={setTransferMember}
              />
            ))}
          </TeamTableBody>
        </TeamTable>
      )}
      {transferMember && (
        <TransferOwnershipDialog
          memberName={transferMember.name}
          memberEmail={transferMember.email}
          open
          isSubmitting={isTransferring}
          onConfirm={confirmOwnershipTransfer}
          onOpenChange={(open) => {
            if (!open) {
              setTransferMember(null);
            }
          }}
        />
      )}
    </div>
  );
};

export default TeamMembers;

interface TeamMemberRowProps {
  item: TeamMember;
  index: number;
  displaylist: TeamMember[];
  setAllUsers: (users: TeamMember[]) => void;
  allUsers: TeamMember[];
  showActions: boolean;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  loadMembers: (organizationId: string) => Promise<void>;
  canTransferOwnership: boolean;
  onTransferOwnership: (member: TeamMember) => void;
}

function TeamMemberRow({
  item,
  index,
  displaylist,
  allUsers,
  setAllUsers,
  showActions,
  isLoading,
  setIsLoading,
  loadMembers,
  canTransferOwnership,
  onTransferOwnership,
}: TeamMemberRowProps) {
  const userContext = useContext(UserContext);
  const isLastRow = index === displaylist.length - 1;

  const deletePermission = async (member: TeamMember) => {
    if (member.level === OrgRoleIds.Owner || isLoading) {
      // Skip if the user is the org owner or a request is already in progress
      return;
    }
    setIsLoading(true);
    try {
      await organizationAccessClient.removeAccess(
        member.access.organizationId,
        member.id,
      );
      // If the user removed themselves, reload the app to refresh their visible orgs
      if (member.id === userContext.me?.id) {
        window.localStorage.removeItem(LOCALSTORAGE_ACTIVE_ORG_ID_KEY);
        window.location.reload();
        return;
      }

      // Otherwise update the list of users
      const index = allUsers.findIndex((user) => user.id === member.id);
      if (index > -1) {
        const newUsers = [...allUsers];
        newUsers.splice(index, 1);
        setAllUsers(newUsers);
      }
    } catch (error) {
      toast.error("Unable to delete member", {
        description: `An unexpected error occurred. Contact support at ${BREV_SUPPORT_EMAIL}.`,
      });
    }
    setIsLoading(false);
  };

  const updateRole = async (member: TeamMember, role: OrgRoleIds) => {
    setIsLoading(true);
    try {
      await organizationAccessClient.setAccess(
        member.access.organizationId,
        member.id,
        orgRoleToOrganizationAccessLevel(role),
      );
      await loadMembers(member.access.organizationId);
    } catch (error) {
      toast.error("Unable to change member level", {
        description: `An unexpected error occurred. Contact support at ${BREV_SUPPORT_EMAIL}.`,
      });
    }
    setIsLoading(false);
  };

  return (
    <tr>
      <TeamTableCell isLastRow={isLastRow}>
        <div className="w-15">
          <div className="w-10 h-10 rounded-full bg-highlight flex items-center justify-center">
            <span className="text-white font-semibold">
              {item.name ? item.name[0].toLocaleUpperCase() : "?"}
            </span>
          </div>
        </div>
      </TeamTableCell>
      <TeamTableCell isLastRow={isLastRow}>
        <div className="w-15">{item.name}</div>
      </TeamTableCell>
      <TeamTableCell isLastRow={isLastRow}>
        <div className="w-15">{item.email}</div>
      </TeamTableCell>
      <TeamTableCell isLastRow={isLastRow}>
        <div className="w-15">
          {getRoleDisplayName(item.level) ?? "Unknown"}
        </div>
      </TeamTableCell>
      {showActions && (
        <TeamTableCell isLastRow={isLastRow}>
          <div className="w-15 relative top-[2px]">
            {item.level !== OrgRoleIds.Owner && (
              <DropdownMenu>
                <DropdownMenuTrigger>
                  <EllipsisHorizontalIcon className="w-8 h-8 hover:bg-gray-50 dark:hover:bg-neutral-700 rounded" />
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {item.level === OrgRoleIds.Member ? (
                    <DropdownMenuItem
                      className="flex flex-row items-center gap-2"
                      onClick={() => updateRole(item, OrgRoleIds.Admin)}
                    >
                      <UserPlusIcon className="w-4 h-4" />
                      Promote to admin
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem
                      className="flex flex-row items-center gap-2"
                      onClick={() => updateRole(item, OrgRoleIds.Member)}
                    >
                      <UserMinusIcon className="w-5 h-5" />
                      Demote to member
                    </DropdownMenuItem>
                  )}
                  {canTransferOwnership && (
                    <DropdownMenuItem
                      className="flex flex-row items-center gap-2"
                      onClick={() => onTransferOwnership(item)}
                    >
                      <ArrowsRightLeftIcon className="w-5 h-5" />
                      Transfer ownership
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    className="flex flex-row items-center gap-2 !text-red-500 hover:!bg-red-900 hover:!text-white"
                    onClick={() => deletePermission(item)}
                  >
                    <ExclamationTriangleIcon className="w-5 h-5" />
                    {item.id === userContext.me?.id
                      ? "Leave team"
                      : "Remove from team"}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </TeamTableCell>
      )}
    </tr>
  );
}
